// Protocolo do Hábito — a sessão sem decisões e a celebração imediata.
//
// "Ao abrir o app na hora de ler, o usuário deve encontrar o livro já aberto na
// página certa — não uma tela de escolhas. Deliberação no momento da execução é
// protetora contra a formação de hábito."
//
// Por isso aqui não existe escolher livro, escolher modo, ajustar meta nem
// configurar nada. Existe o livro, a página e um botão. No fim, UMA pergunta,
// já preenchida com o mínimo — um toque em "Salvar" é uma resposta válida e
// honesta, porque uma página vence o dia.

import React, { useEffect, useRef, useState } from 'react';
import { BookCover } from '../common';
import { Icon } from '../icons';
import { LivroDoDia } from './api';

const serif = 'Instrument Serif, Georgia, serif';
const CREME = '#F5F1EA';

const eyebrow: React.CSSProperties = {
  fontSize: 10.5, letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 600,
};

export const SessaoSemDecisoes: React.FC<{
  livro: LivroDoDia;
  minimoPaginas: number;
  onFechar: () => void;
  /** `inicio` so vem quando a pessoa ajustou a pagina de comeco. O app
   *  precisa dele ANTES de somar a leitura, senao a contagem sai errada. */
  onSalvar: (pagina: number, minutos: number, inicio?: number) => Promise<void> | void;
}> = ({ livro, minimoPaginas, onFechar, onSalvar }) => {
  const [fase, setFase] = useState<'lendo' | 'registrar'>('lendo');
  // Onde a leitura REALMENTE comeca. Livro nenhum comeca na 0: prefacio,
  // sumario e dedicatoria empurram o inicio para a 9, a 15, a 23. Sem
  // perguntar isso, a primeira leitura ja nasce com a contagem errada e todo
  // o progresso seguinte herda o erro.
  const [paginaInicio, setPaginaInicio] = useState<number>(livro.paginaAtual || 1);
  const [ajustandoInicio, setAjustandoInicio] = useState(true);
  const [segundos, setSegundos] = useState(0);
  const [pagina, setPagina] = useState(Math.max(livro.paginaAtual + minimoPaginas, 1));
  const [salvando, setSalvando] = useState(false);
  const inicio = useRef(Date.now());

  useEffect(() => {
    const t = setInterval(() => setSegundos(Math.floor((Date.now() - inicio.current) / 1000)), 1000);
    return () => clearInterval(t);
  }, []);

  // Mesma medida do segBtn do LogReadingSheet (36x40, raio 10) — e o botao
  // que a pessoa vai apertar todo dia no registro normal.
  const ctrlBtn: React.CSSProperties = {
    width: 40, height: 44, borderRadius: 12, flex: 'none',
    background: 'rgba(245,241,234,0.06)', border: '1px solid rgba(245,241,234,0.16)',
    color: CREME, fontSize: 20, lineHeight: 1, cursor: 'pointer',
  };

  const mm = String(Math.floor(segundos / 60)).padStart(2, '0');
  const ss = String(segundos % 60).padStart(2, '0');
  const minutos = Math.max(1, Math.round(segundos / 60));
  // De onde a conta parte — a MESMA regra do App ao salvar (`antes`): a página
  // de início, se a pessoa a ajustou; senão a última registrada. Contar de
  // outro ponto aqui fazia o "+10" virar "+11 páginas" num livro novo (pente
  // fino, QA-08), e a celebração dizer outro número.
  const base = paginaInicio !== (livro.paginaAtual || 1) ? paginaInicio : livro.paginaAtual;
  const teto = livro.totalPaginas || 99999;
  const lidas = Math.max(0, Math.min(pagina, teto) - base);

  async function salvar() {
    setSalvando(true);
    // Só manda o início quando ele mudou de fato — assim o App sabe que
    // precisa sincronizar a posição antes de registrar a leitura.
    const inicioMudou = paginaInicio !== (livro.paginaAtual || 1);
    try { await onSalvar(Math.min(pagina, teto), minutos, inicioMudou ? paginaInicio : undefined); }
    finally { setSalvando(false); }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9700, background: '#141110',
      display: 'flex', flexDirection: 'column', maxWidth: 460, margin: '0 auto',
      animation: 'e2FadeUp .3s ease both',
    }}>
      <style>{`
        @keyframes habBrasa { 0%,100% { opacity:.35; transform: scale(1) } 50% { opacity:.65; transform: scale(1.05) } }
      `}</style>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'calc(14px + env(safe-area-inset-top,0px)) 18px 0' }}>
        <button onClick={onFechar} style={{
          width: 38, height: 38, borderRadius: '50%', border: 'none',
          background: 'rgba(245,241,234,0.08)', display: 'grid', placeItems: 'center', cursor: 'pointer',
        }}>
          <Icon name="close" size={17} color="rgba(245,241,234,0.7)" />
        </button>
        <div className="mono" style={{ ...eyebrow, color: 'rgba(245,241,234,0.35)' }}>
          {/* "Modo leitura" fazia parecer que o texto viria aqui. */}
          {fase === 'lendo' ? 'Com o livro na mão' : 'Só isso'}
        </div>
        <div style={{ width: 38 }} />
      </div>

      {fase === 'lendo' && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '0 28px', textAlign: 'center' }}>
          <div style={{ position: 'relative', marginBottom: 26 }}>
            <div style={{
              position: 'absolute', inset: -26, borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(232,122,59,0.30), transparent 70%)',
              animation: 'habBrasa 5s ease-in-out infinite',
            }} />
            <div style={{ position: 'relative' }}>
              <BookCover book={{ title: livro.titulo, coverUrl: livro.capaUrl, coverHue: livro.capaHue, coverStyle: livro.capaEstilo } as any} width={118} height={177} />
            </div>
          </div>

          <div style={{ fontFamily: serif, fontSize: 25, lineHeight: 1.2, color: CREME }}>{livro.titulo}</div>
          <div style={{ fontSize: 14.5, color: 'rgba(245,241,234,0.55)', marginTop: 8 }}>
            {livro.paginaAtual > 0 ? `Abra na página ${livro.proximaPagina}.` : 'Abra na primeira página.'}
          </div>
          <div style={{ fontSize: 14.5, color: 'rgba(245,241,234,0.4)', marginTop: 22, lineHeight: 1.55, maxWidth: 280 }}>
            Deixe o celular de lado. Uma página já vence o dia — o resto é lucro.
          </div>
        </div>
      )}

      {fase === 'registrar' && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '0 26px' }}>

          {/* Mesmo vocabulario do LogReadingSheet: eyebrow mono, titulo serif,
              numero grande em ember. Quem aprende aqui reconhece la — e o
              registro de leitura e o gesto que essa pessoa vai repetir todo
              dia pelos proximos 67. */}
          <div className="mono" style={{ ...eyebrow, fontSize: 10, color: 'rgba(245,241,234,0.5)' }}>
            {ajustandoInicio ? 'Antes de registrar' : 'Registrar'}
          </div>
          <div style={{ fontFamily: serif, fontSize: 26, lineHeight: 1.2, color: CREME, margin: '8px 0 6px' }}>
            {ajustandoInicio ? 'Em que página você começou?' : 'Até onde você leu?'}
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.5, color: 'rgba(245,241,234,0.45)', marginBottom: 24 }}>
            {ajustandoInicio
              ? 'Quase nenhum livro começa na primeira folha. Diga onde o texto começa de verdade — é isso que faz a contagem bater.'
              : 'Se foi só o mínimo, você venceu o dia do mesmo jeito.'}
          </div>

          {/* controle no padrao do sheet: botao 36x40 + numero central */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center' }}>
            <button
              onClick={() => ajustandoInicio
                ? setPaginaInicio((p) => Math.max(1, p - 1))
                : setPagina((p) => Math.max(paginaInicio, p - 1))}
              style={ctrlBtn}
            >−</button>

            <div style={{
              flex: 1, maxWidth: 190, padding: '14px 10px', borderRadius: 14,
              background: 'rgba(245,241,234,0.05)', border: '1px solid rgba(245,241,234,0.12)',
              textAlign: 'center',
            }}>
              <input
                type="number"
                inputMode="numeric"
                value={ajustandoInicio ? paginaInicio : Math.min(pagina, teto)}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!Number.isFinite(v)) return;
                  if (ajustandoInicio) setPaginaInicio(Math.max(1, Math.min(teto, v)));
                  else setPagina(Math.max(paginaInicio, Math.min(teto, v)));
                }}
                style={{
                  width: '100%', border: 'none', background: 'transparent', outline: 'none',
                  textAlign: 'center', color: 'var(--ember)', fontWeight: 600,
                  fontSize: 34, lineHeight: 1, WebkitAppearance: 'none', MozAppearance: 'textfield',
                }}
              />
              <div style={{ ...eyebrow, fontSize: 9, color: 'rgba(245,241,234,0.4)', marginTop: 7 }}>
                {ajustandoInicio
                  ? 'página de início'
                  : (lidas > 0 ? `+${lidas} ${lidas === 1 ? 'página' : 'páginas'}` : 'página')}
              </div>
            </div>

            <button
              onClick={() => ajustandoInicio
                ? setPaginaInicio((p) => Math.min(teto, p + 1))
                : setPagina((p) => Math.min(teto, p + 1))}
              style={ctrlBtn}
            >+</button>
          </div>

          {!ajustandoInicio && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginTop: 16 }}>
              {[5, 10, 20, 30].map((n) => (
                <button key={n} onClick={() => setPagina(Math.min(teto, Math.max(paginaInicio, base + n)))} style={{
                  padding: '9px 15px', borderRadius: 999, border: '1px solid rgba(245,241,234,0.16)',
                  background: 'transparent', color: 'rgba(245,241,234,0.75)', fontSize: 13.5, cursor: 'pointer',
                }}>+{n}</button>
              ))}
            </div>
          )}

          {/* Sair do ajuste e uma escolha, nao um passo obrigatorio: quem abriu
              o livro na primeira folha mesmo nao precisa mexer em nada. */}
          {ajustandoInicio && (
            <button
              onClick={() => { setPagina(Math.max(paginaInicio + minimoPaginas, paginaInicio + 1)); setAjustandoInicio(false); }}
              style={{
                background: 'none', border: 'none', color: 'rgba(245,241,234,0.45)',
                fontSize: 13.5, padding: '18px 0 0', cursor: 'pointer', textAlign: 'center',
              }}>Comecei na página {paginaInicio} — continuar</button>
          )}
        </div>
      )}

      <div style={{ padding: '10px 26px calc(22px + env(safe-area-inset-bottom,0px))' }}>
        <button
          onClick={() => {
            if (fase === 'lendo') { setFase('registrar'); return; }
            if (ajustandoInicio) {
              setPagina(Math.max(paginaInicio + minimoPaginas, paginaInicio + 1));
              setAjustandoInicio(false);
              return;
            }
            salvar();
          }}
          disabled={salvando}
          style={{
            width: '100%', padding: '17px 20px', borderRadius: 999, border: 'none',
            background: 'var(--ember)', color: '#fff', fontSize: 16, fontWeight: 600, cursor: 'pointer',
            boxShadow: '0 14px 32px -14px var(--ember)',
          }}
        >
          {salvando ? 'um instante…' : fase === 'lendo' ? 'Terminei de ler' : ajustandoInicio ? 'Confirmar' : 'Salvar'}
        </button>
        <div className="mono" style={{ textAlign: 'center', fontSize: 11, color: 'rgba(245,241,234,0.25)', marginTop: 12, letterSpacing: '0.1em' }}>
          {mm}:{ss}
        </div>
      </div>
    </div>
  );
};

/* ── A celebração ────────────────────────────────────────────────────────
   "O que fecha o loop não é anotar o progresso — é o reconhecimento imediato
   logo após a sessão. Os dez segundos depois de fechar o livro são tão
   importantes quanto os trinta segundos antes de abrir."

   Dois modos de reforço, por perfil (correção 6): para quem já gosta de ler,
   recompensa tangível corrói a motivação que já existe, então o reconhecimento
   é de significado. Para quem está criando o gosto, o reforço externo é seguro
   na ativação. Não é refinamento de texto — é bifurcação de arquitetura. */
export const CelebracaoDoDia: React.FC<{
  dia: number | null;
  paginas: number;
  perfil: 'reencontro' | 'ignicao' | null;
  sequencia: { atual: number; melhor: number; diasLidos: number };
  primeiroDia?: boolean;
  // Invariante 5: quem volta depois de faltar recebe MAIS reconhecimento do que
  // quem nunca faltou. Entre 53 intervenções testadas com 61.293 pessoas, foi
  // esta a vencedora. Quando este campo vem preenchido, a celebração muda de
  // tamanho — não é a mesma festa com outra frase.
  retorno?: { dias: number; total?: number } | null;
  recorde?: boolean;
  onFechar: () => void;
}> = ({ dia, paginas, perfil, sequencia, primeiroDia, retorno, recorde, onFechar }) => {
  useEffect(() => {
    const t = setTimeout(onFechar, retorno ? 6000 : 4200);
    return () => clearTimeout(t);
  }, [onFechar, retorno]);

  // Recompensa 100% previsível para de ensinar. A frase precisa acumular alguma
  // coisa que a pessoa não viu ontem — senão, na nona vez, ela é papel de parede.
  const frase = retorno
    ? (retorno.total && retorno.total >= 3
        ? `${retorno.total}ª volta. Voltar depois de faltar é a parte mais difícil deste programa, e é nela que você é bom.`
        : 'Voltar depois de faltar é a coisa mais difícil deste programa — e é a que mais conta. Quem faz isso é quem chega ao fim.')
    : recorde
      ? `${sequencia.atual} dias seguidos — é a sua maior sequência até hoje.`
    : primeiroDia
      ? 'O primeiro dia é o que mais vale. A curva sobe mais aqui do que em qualquer outro ponto.'
      : perfil === 'ignicao'
        ? (sequencia.atual >= 2 ? `${sequencia.atual} dias seguidos. Você está construindo algo que ainda nem sente.` : 'Mais um dia no lugar certo.')
        : (sequencia.atual >= 2 ? `${sequencia.atual} dias. O mesmo lugar, a mesma hora — é isso que forma o hábito.` : 'De volta ao seu lugar.');

  return (
    <div onClick={onFechar} style={{
      position: 'fixed', inset: 0, zIndex: 9800, background: '#141110',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      textAlign: 'center', padding: 32, cursor: 'pointer', animation: 'e2FadeUp .35s ease both',
    }}>
      <style>{`
        @keyframes habHalo { 0% { transform: scale(.4); opacity: 0 } 45% { opacity:.55 } 100% { transform: scale(2.6); opacity: 0 } }
        @keyframes habSobe { 0% { transform: translateY(14px); opacity: 0 } 100% { transform: translateY(0); opacity: 1 } }
      `}</style>

      <div style={{ position: 'relative', width: retorno ? 168 : 128, height: retorno ? 168 : 128, display: 'grid', placeItems: 'center', marginBottom: 26 }}>
        {(retorno ? [0, 0.4, 0.8, 1.2, 1.6] : [0, 0.55, 1.1]).map((d, k) => (
          <div key={k} style={{
            position: 'absolute', inset: retorno ? 34 : 26, borderRadius: '50%', border: '1px solid var(--ember)',
            animation: `habHalo ${retorno ? 2.2 : 2.6}s ${d}s ease-out infinite`,
          }} />
        ))}
        <Icon name="flame" size={retorno ? 76 : 58} color="var(--ember)" />
      </div>

      <div className="mono" style={{ ...eyebrow, color: retorno ? 'var(--ember)' : 'rgba(245,241,234,0.45)', animation: 'habSobe .5s .1s both' }}>
        {retorno
          ? (retorno.dias === 1 ? 'Depois de 1 dia fora' : `Depois de ${retorno.dias} dias fora`)
          : dia ? `Dia ${dia}` : 'Registrado'}
      </div>
      <div style={{
        fontFamily: serif, fontSize: retorno ? 46 : 40, lineHeight: 1.05, color: CREME,
        margin: '10px 0 12px', animation: 'habSobe .5s .2s both',
      }}>
        {retorno ? 'Você voltou.' : primeiroDia ? 'Começou.' : 'Está feito.'}
      </div>
      <div style={{ fontSize: 15.5, color: 'rgba(245,241,234,0.7)', animation: 'habSobe .5s .3s both' }}>
        +{paginas} {paginas === 1 ? 'página' : 'páginas'}
      </div>
      <div style={{
        fontSize: 14.5, lineHeight: 1.6, color: 'rgba(245,241,234,0.45)',
        maxWidth: 300, marginTop: 20, animation: 'habSobe .5s .45s both',
      }}>
        {frase}
      </div>
    </div>
  );
};
